import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import { MessageCircle, Menu, X, Sparkles, Sun, Moon } from 'lucide-react';
import { Button } from '../ui/button';
import { useWhatsAppDemo } from '../../hooks/useWhatsAppDemo';
import { useTheme } from '../../lib/theme';

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isDark, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    setIsMobileMenuOpen(false);
    if (location.pathname !== '/') {
      navigate(`/#${sectionId}`);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleDemoRequest = useWhatsAppDemo();

  const handleLogoClick = useCallback(() => {
    navigate('/');
    window.scrollTo(0, 0);
  }, [navigate]);

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
          ? 'bg-background/90 backdrop-blur-sm shadow-lg border-b border-border'
          : 'bg-transparent'
        }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <motion.div
            className="flex items-center space-x-2 cursor-pointer"
            whileHover={{ scale: 1.05 }}
            onClick={handleLogoClick}
          >
            <div className="relative">
              <MessageCircle className="h-8 w-8 text-primary" aria-hidden="true" />
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-success rounded-full"></div>
            </div>
            <span className="text-2xl font-heading font-bold text-text-primary">
              Chat<span className="text-primary">Ly</span>
            </span>
          </motion.div>

          <nav className="hidden lg:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('home')}
              className="text-text-primary hover:text-primary transition-colors font-medium"
            >
              Home
            </button>
            <button
              onClick={() => scrollToSection('features')}
              className="text-text-primary hover:text-primary transition-colors font-medium"
            >
              Funzionalità
            </button>
            <button
              onClick={() => scrollToSection('settori')}
              className="text-text-primary hover:text-primary transition-colors font-medium"
            >
              Casi d'uso
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="text-text-primary hover:text-primary transition-colors font-medium"
            >
              Chi Siamo
            </button>
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Cambia tema"
              className="rounded-full"
              onClick={toggle}
            >
              {isDark ? (
                <Sun className="h-5 w-5" aria-hidden="true" />
              ) : (
                <Moon className="h-5 w-5" aria-hidden="true" />
              )}
            </Button>
            <Button
              onClick={handleDemoRequest}
              variant="gradient"
              size="lg"
              className="rounded-full font-medium gap-2"
            >
              <Sparkles className="h-5 w-5" aria-hidden="true" />
              Richiedi Demo
            </Button>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-text-primary hover:text-primary transition-colors"
            aria-label="Apri o chiudi il menu"
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6" aria-hidden="true" />
            ) : (
              <Menu className="h-6 w-6" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      <motion.div
        id="mobile-menu"
        initial={{ opacity: 0, height: 0 }}
        animate={{
          opacity: isMobileMenuOpen ? 1 : 0,
          height: isMobileMenuOpen ? 'auto' : 0
        }}
        className={`lg:hidden bg-background border-t border-border overflow-hidden`}
      >
        <div className="container mx-auto px-4 py-4 space-y-4">
          <button
            onClick={() => scrollToSection('home')}
            className="block w-full text-left text-text-primary hover:text-primary transition-colors font-medium py-2"
          >
            Home
          </button>
          <button
            onClick={() => scrollToSection('features')}
            className="block w-full text-left text-text-primary hover:text-primary transition-colors font-medium py-2"
          >
            Funzionalità
          </button>
          <button
            onClick={() => scrollToSection('settori')}
            className="block w-full text-left text-text-primary hover:text-primary transition-colors font-medium py-2"
          >
            Casi d'uso
          </button>
          <button
            onClick={() => scrollToSection('about')}
            className="block w-full text-left text-text-primary hover:text-primary transition-colors font-medium py-2"
          >
            Chi Siamo
          </button>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Cambia tema"
            className="rounded-full"
            onClick={toggle}
          >
            {isDark ? (
              <Sun className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Moon className="h-5 w-5" aria-hidden="true" />
            )}
          </Button>
          <Button
            onClick={handleDemoRequest}
            variant="gradient"
            size="lg"
            className="w-full rounded-full font-medium mt-4 gap-2"
          >
            <Sparkles className="h-5 w-5" aria-hidden="true" />
            Richiedi Demo
          </Button>
        </div>
      </motion.div>
    </motion.header>
  );
};

export default Header;
